/**
 * RadioField - Radio button group field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Radio button group with options from field.options.
 * Only one option can be selected at a time.
 * form-embed D13: a question set to Chips draws its options as pills, still radio inputs.
 */

import type { ChangeEvent } from 'react';
import type { RadioFieldProps } from './types';
import { useFieldPart } from './fieldParts';
import { FieldWrapper } from './FieldWrapper';
import styles from './fields.module.css';

/**
 * RadioField renders a group of radio buttons.
 */
export function RadioField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: RadioFieldProps) {
  const options = field.options ?? [];
  const part = useFieldPart();

  // A question shows as the studio set it: rows with radio buttons (List), or pills (Chips). The automatic segmented
  // toggle for 2 or 3 short answers is gone (card 4 form walk, Gabriel 2026-11-01: "it should show either radio
  // options or the chips").
  const chips = field.display === 'chips';

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      style={style}
      isGroup
    >
      <div
        className={`${styles.radioGroup}${chips ? ` ${styles.chips}` : ''}`}
        role="radiogroup"
        aria-labelledby={`${field.fieldId}-label`}
      >
        {options.map((option) => {
          const isSelected = value === option.value;
          const optionId = `${field.fieldId}-${option.value}`;

          return (
            <label key={option.value} className={`${styles.radioOption}${chips && isSelected ? ` ${styles.chipOn}` : ''}`} {...part('choice', isSelected)}>
              <input
                type="radio"
                id={optionId}
                name={field.fieldId}
                value={option.value}
                className={styles.radioInput}
                checked={isSelected}
                onChange={handleChange}
                onBlur={onBlur}
                aria-disabled={nonInteractive || undefined}
                aria-invalid={!!error}
                style={nonInteractive ? { pointerEvents: 'none' } : undefined}
              />
              <span
                className={`${styles.radioBox} ${error ? styles.hasError : ''}`}
              />
              <span className={styles.radioLabel}>{option.label}</span>
            </label>
          );
        })}
      </div>
    </FieldWrapper>
  );
}
